import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useDragControls, useReducedMotion } from 'framer-motion';
import { useLang } from '../i18n';
import { spring } from './motion';

const copy = {
  scrollHint: { en: 'Scroll inside to explore', ar: 'مرّر داخل الشاشة للاستكشاف' },
  viewFull: { en: 'View full page', ar: 'عرض الصفحة كاملة' },
  desktop: { en: 'Desktop', ar: 'الكمبيوتر' },
  mobile: { en: 'Mobile', ar: 'الجوال' },
  recording: { en: 'Recording', ar: 'تسجيل' },
  screens: { en: 'Screenshots', ar: 'لقطات الشاشة' },
  close: { en: 'Close', ar: 'إغلاق' },
};

// A screenshot inside a screen. It starts with the light first-screen
// preview; on devices with a mouse or trackpad, the full-length capture loads
// the moment someone reaches for it, and then scrolls in place.
function Screen({ src, preview, size, alt, scrollable, priority, rounded }) {
  const [full, setFull] = useState(!preview);
  const loadFull = () => scrollable && setFull(true);
  return (
    <div
      className={`hide-scrollbar h-full w-full overflow-hidden bg-white ${rounded} ${
        scrollable ? '[@media(hover:hover)]:overflow-y-auto [@media(hover:hover)]:overscroll-contain' : ''
      }`}
      tabIndex={scrollable ? 0 : undefined}
      aria-label={scrollable ? alt : undefined}
      onPointerEnter={loadFull}
      onFocus={loadFull}
    >
      <img
        src={full ? src : preview}
        alt={alt}
        width={size.width}
        height={full ? undefined : size.height}
        loading={priority ? 'eager' : 'lazy'}
        fetchpriority={priority ? 'high' : undefined}
        decoding="async"
        className="block h-auto w-full"
      />
    </div>
  );
}

export function Laptop({ src, preview, video, alt, scrollable = true, priority = false, className = '' }) {
  const reduce = useReducedMotion();
  return (
    <div className={className} dir="ltr">
      <div className="rounded-[1.1rem] bg-[#1d1d1f] p-[1.6%] shadow-[0_30px_60px_-30px_rgba(11,11,11,0.35)]">
        <div className="relative aspect-[16/10] overflow-hidden rounded-[0.45rem]">
          {video ? (
            <video
              className="h-full w-full object-cover object-top"
              src={video}
              autoPlay={!reduce}
              controls={reduce}
              muted
              loop
              playsInline
              aria-label={alt}
            />
          ) : (
            <Screen
              src={src}
              preview={preview}
              size={{ width: 1440, height: 900 }}
              alt={alt}
              scrollable={scrollable}
              priority={priority}
              rounded="rounded-[0.45rem]"
            />
          )}
        </div>
      </div>
      <div className="relative mx-[-5%] h-[0.9rem] rounded-b-[1rem] bg-gradient-to-b from-[#d8d8d3] to-[#b9b9b3]">
        <div className="absolute left-1/2 top-0 h-[35%] w-[14%] -translate-x-1/2 rounded-b-md bg-[#a7a7a1]" />
      </div>
    </div>
  );
}

export function Phone({ src, preview, alt, scrollable = true, priority = false, className = '' }) {
  return (
    <div className={className} dir="ltr">
      <div className="relative rounded-[2.4rem] bg-[#1d1d1f] p-[3.2%] shadow-[0_30px_60px_-25px_rgba(11,11,11,0.4)]">
        <div className="relative aspect-[390/844] overflow-hidden rounded-[2rem]">
          <Screen
            src={src}
            preview={preview}
            size={{ width: 390, height: 844 }}
            alt={alt}
            scrollable={scrollable}
            priority={priority}
            rounded="rounded-[2rem]"
          />
          <div className="pointer-events-none absolute left-1/2 top-[1.6%] h-[3.2%] w-[30%] -translate-x-1/2 rounded-full bg-[#1d1d1f]" />
        </div>
      </div>
    </div>
  );
}

// Segmented control; the selected pill glides between options on a spring.
export function Segmented({ options, value, onChange, label, id }) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex rounded-full bg-cream-soft p-1">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className="press relative rounded-full px-4 py-1.5 text-sm font-medium"
        >
          {value === o.value && (
            <motion.span layoutId={`seg-${id}`} className="absolute inset-0 rounded-full bg-white shadow-sm" transition={spring.ui} />
          )}
          <span className={`relative ${value === o.value ? 'text-ink' : 'text-muted'}`}>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// Full-height sheet for reading a capture at full size. Drag the handle
// down (or flick it) to dismiss; it rubber-bands if dragged upward.
function Sheet({ title, views, onClose }) {
  const { t } = useLang();
  const controls = useDragControls();
  const [view, setView] = useState(views[0].value);
  const closeRef = useRef(null);
  const current = views.find((v) => v.value === view);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={title}>
      <motion.div
        className="absolute inset-0 bg-ink/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={spring.ui}
        onClick={onClose}
      />
      <motion.div
        className="absolute inset-x-0 bottom-0 top-6 flex flex-col overflow-hidden rounded-t-[1.75rem] bg-white md:inset-x-[8%] md:top-10"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={spring.sheet}
        drag="y"
        dragListener={false}
        dragControls={controls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0.05, bottom: 0.9 }}
        onDragEnd={(_, info) => {
          // Decide by where the flick is heading, not just where it was let go.
          if (info.velocity.y > 500 || info.offset.y + info.velocity.y * 0.2 > 160) onClose();
        }}
      >
        <div
          className="flex cursor-grab touch-none flex-col items-center gap-3 border-b border-line px-5 pb-4 pt-3 active:cursor-grabbing"
          onPointerDown={(e) => controls.start(e)}
        >
          <span className="h-1.5 w-10 rounded-full bg-ink/15" aria-hidden="true" />
          <div className="flex w-full items-center justify-between gap-4">
            <span className="truncate font-semibold">{title}</span>
            <div className="flex items-center gap-3">
              {views.length > 1 && <Segmented id="sheet" label={title} options={views} value={view} onChange={setView} />}
              <button ref={closeRef} type="button" onClick={onClose} className="press rounded-full bg-cream-soft px-4 py-1.5 text-sm font-medium">
                {t(copy.close)}
              </button>
            </div>
          </div>
        </div>
        <div className="hide-scrollbar flex-1 overflow-y-auto overscroll-contain bg-cream-soft">
          <img
            src={current.src}
            alt={`${title}: ${current.label}`}
            className={`mx-auto block w-full bg-white ${view === 'mobile' ? 'max-w-[390px]' : 'max-w-[1440px]'}`}
          />
        </div>
      </motion.div>
    </div>
  );
}

// Laptop and phone together, showing the screenshots captured at handover.
export function DeviceShowcase({ name, capture, className = '' }) {
  const { t } = useLang();
  const [mode, setMode] = useState('screens');
  const [sheet, setSheet] = useState(false);
  const closeSheet = useCallback(() => setSheet(false), []);
  const alt = (label) => `${name} – ${label}`;

  return (
    <div className={className}>
      <div className="relative pe-[12%] pb-[6%] sm:pe-[14%]">
        <Laptop
          src={capture.desktop}
          preview={capture.previewDesktop}
          video={mode === 'video' ? capture.video : null}
          alt={alt(t(copy.desktop))}
          priority
        />
        <Phone
          src={capture.mobile}
          preview={capture.previewMobile}
          alt={alt(t(copy.mobile))}
          priority
          className="absolute bottom-0 end-0 w-[22%] min-w-[84px]"
        />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {capture.video && (
          <Segmented
            id={`mode-${name}`}
            label={name}
            value={mode}
            onChange={setMode}
            options={[
              { value: 'screens', label: t(copy.screens) },
              { value: 'video', label: t(copy.recording) },
            ]}
          />
        )}
        <button type="button" onClick={() => setSheet(true)} className="btn-secondary px-5 py-2 text-sm">
          {t(copy.viewFull)}
        </button>
        <span className="hidden text-sm text-muted [@media(hover:hover)]:inline">{t(copy.scrollHint)}</span>
      </div>
      <AnimatePresence>
        {sheet && (
          <Sheet
            title={name}
            onClose={closeSheet}
            views={[
              { value: 'desktop', label: t(copy.desktop), src: capture.desktop },
              { value: 'mobile', label: t(copy.mobile), src: capture.mobile },
            ]}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
